import { z } from 'zod';

import { sectionBaseSchema } from '../base';
import { numberWithUnitSchema } from '../../../mixins';

/**
 * An ordinary row carries only the two layout knobs its columns are laid out
 * with.
 *
 * `columnGap` is the space between neighbouring columns. It is not applied as a
 * `gap`: half of it becomes each column's horizontal margin and half the vertical
 * margin of the cell around it, so the value is what the user sees between two
 * columns either way. The header row spells the same idea `columnGap` too.
 *
 * The AI surface has NO section field — `packages/ai-connector` carries a schema
 * for the block and for the column but none for the row, so neither knob here is
 * reachable from a chat turn. `columnGap` in particular is a visible layout
 * setting the editor exposes on the COLUMN panel (it reads through to the parent
 * row), which is where an AI surface for it would naturally go.
 *
 * **Nothing on a row is per-breakpoint.** The legacy JSON never carried a
 * per-device row value, no panel offers a device toggle for one, and the CSS has
 * no per-device row rule — so the fields here are flat. `isFlexibleWidths` is
 * flat for a second reason on top: it picks which unit the columns store their
 * width in, and a per-breakpoint unit would be meaningless.
 *
 * The block is the entity that is responsive; a row inherits its breakpoint from
 * the block it sits in.
 */
export const sectionDefaultDataSchema = z.object({
    columnGap: numberWithUnitSchema,
    isFlexibleWidths: z.boolean(),
});

export const sectionDefaultSchema = sectionBaseSchema.extend({
    entitySubType: z.literal('default'),
    props: sectionDefaultDataSchema,
    version: z.literal(0),
});

export const sectionDefaultV0Schema = sectionDefaultSchema;
export type SectionDefaultV0 = z.infer<typeof sectionDefaultV0Schema>;
